import { fetchBaseQuery, createApi, TagDescription } from '@reduxjs/toolkit/query/react';
import { Periodicity } from 'src/interfaces/entities/periodicity';
import { ZapiConfig } from 'src/interfaces/entities/zapi-config';
import { User } from 'src/interfaces/entities/user';
import { WhatsappGroup } from 'src/interfaces/entities/whatsappGroup';
import { Partner } from 'src/interfaces/entities/partners';
import { Severity } from 'src/interfaces/entities/severity';
import { Observer, ObserverReport } from 'src/interfaces/entities/observer';
import { ObserverWhatsappGroup } from 'src/interfaces/entities/observer-whatsapp-group';

import { CustomDashboard } from 'src/interfaces/entities/custom-dashboard';
import { Occurrence } from 'src/interfaces/entities/occurrence';
import { OcurrenceData } from 'src/interfaces/entities/occurence-data';
import { Module, ModuleView } from 'src/interfaces/entities/module';
import { BaileysPhoneConfig } from 'src/interfaces/entities/baileys-phone-config';

import { Organization } from 'src/interfaces/entities/organization';

import Cookies from 'js-cookie';

export const centralApi = createApi({
	baseQuery: fetchBaseQuery({
		baseUrl: process.env.REACT_APP_API_URL,
		prepareHeaders: (headers, api) => {
			console.log('Preparing headers');
			const token = Cookies.get('intraguard.token');
			const superToken = Cookies.get('intraguard.super_token');

			if (api.endpoint != 'refreshToken' && api.endpoint != 'authenticateUser')
				token && headers.set('Authorization', `Bearer ${token}`);

			if (api.endpoint == 'changeOrganization' || api.endpoint == 'getOrganizations')
				superToken && headers.set('Authorization', `Bearer ${superToken}`);
		},
	}),
	tagTypes: [
		'all_organizations',
		'observer-whatsapp-group',
		'periodicity',
		'zapi-config',
		'baileys-config',
		'occurrence',
		'wpp-group',
		'observer',
		'severity',
		'module',
		'group',
		'users',
		'custom-dashboard',
		'partners',
		'organizations',
		'auth',
	],
	endpoints: (builder) => ({
		getAllPeriodicities: builder.query<Periodicity<'persisted'>[], void>({
			query: () => ({
				url: `/periodicity/`,
				method: 'GET',
			}),
			providesTags: [ 'periodicity', ],
		}),
		postPeriodicity: builder.mutation<
			void,
			Omit<Periodicity<'not-persisted'>, 'client_id' | 'org_id'>
		>({
			query: (body) => ({
				url: '/periodicity/',
				method: 'POST',
				body,
			}),
			invalidatesTags: [ 'periodicity', ],
		}),
		deletePeriodicity: builder.mutation<void, string>({
			query: (id) => ({
				url: `/periodicity/${id}`,
				method: 'DELETE',
			}),
			invalidatesTags: [ 'periodicity', ],
		}),
		patchPeriodicity: builder.mutation<
			void,
			{
				id: string;
				periodicity: Omit<
					Periodicity<'not-persisted'>,
					'client_id' | 'org_id' | 'creation_date'
				>;
			}
		>({
			query: (payload) => ({
				url: `/periodicity/${payload.id}`,
				method: 'PATCH',
				body: payload.periodicity,
			}),
			invalidatesTags: [ 'periodicity', ],
		}),
		getZapiConfigs: builder.query<ZapiConfig, void>({
			query: () => ({
				url: `/zapi-config/`,
				method: 'GET',
			}),
			providesTags: [ 'zapi-config', ],
		}),
		postZapiConfig: builder.mutation<void, Omit<ZapiConfig, 'clientId'>>({
			query: (body) => ({
				url: '/zapi-config/',
				method: 'POST',
				body,
			}),
			invalidatesTags: [ 'zapi-config', ],
		}),
		deleteZapiConfig: builder.mutation<void, void>({
			query: () => ({
				url: `/zapi-config/`,
				method: 'DELETE',
			}),
			invalidatesTags: [ 'zapi-config', ],
		}),
		patchZapiConfig: builder.mutation<
			void,
			{
				id: string;
				zapiConfig: Omit<ZapiConfig, 'clientId'>;
			}
		>({
			query: (payload) => ({
				url: `/zapi-config/${payload.id}`,
				method: 'PATCH',
				body: payload.zapiConfig,
			}),
			invalidatesTags: [ 'zapi-config', ],
		}),
		// Baileys Phone Config
		getBaileysConfig: builder.query<BaileysPhoneConfig, void>({
			query: () => ({
				url: `/baileys-config/`,
				method: 'GET',
			}),
			transformResponse: (response: { payload: BaileysPhoneConfig }) => response.payload,
			providesTags: [ 'baileys-config', ],
		}),
		postBaileysConfig: builder.mutation<void, { phoneNumber: string }>({
			query: (body) => ({
				url: '/baileys-config/',
				method: 'POST',
				body,
			}),
			invalidatesTags: [ 'baileys-config', ],
		}),
		deleteBaileysConfig: builder.mutation<void, void>({
			query: () => ({
				url: `/baileys-config/`,
				method: 'DELETE',
			}),
			invalidatesTags: [ 'baileys-config', ],
			async onQueryStarted(_, { dispatch, queryFulfilled, }) {
				try {
					await queryFulfilled;
					// Limpa o cache do getBaileysConfig imediatamente
					dispatch(
						centralApi.util.updateQueryData(
							'getBaileysConfig',
							undefined,
							() => undefined as any
						)
					);
				} catch { /* delete failed — cache stays */ }
			},
		}),
		requestBaileysPairingCode: builder.mutation<{ code: string }, { phoneNumber: string }>({
			query: (body) => ({
				url: '/baileys-config/pair',
				method: 'POST',
				body,
			}),
			transformResponse: (response: { payload: { code: string }}) => response.payload,
		}),
		getBaileysQRCode: builder.query<{ qrCode: string }, void>({
			query: () => ({
				url: `/baileys-config/qr`,
				method: 'GET',
			}),
			transformResponse: (response: { payload: { qrCode: string }}) => response.payload,
		}),
		getBaileysStatus: builder.query<{ isConnected: boolean; phoneNumber: string }, void>({
			query: () => ({
				url: `/baileys-config/status`,
				method: 'GET',
			}),
			transformResponse: (
				response: { payload: { isConnected: boolean; phoneNumber: string }}
			) => response.payload,
			providesTags: [ 'baileys-config', ],
		}),
		postBaileysAddToGroups: builder.mutation<{ message: string }, void>({
			query: () => ({
				url: '/baileys-config/add-to-groups',
				method: 'POST',
			}),
		}),
		getBaileysPartnerInfo: builder.query<
			{ hasPartner: boolean; phoneNumber: string | null; isConnected: boolean },
			void
		>({
			query: () => ({
				url: '/baileys-config/partner-info',
				method: 'GET',
			}),
			transformResponse: (
				response: { payload: { hasPartner: boolean; phoneNumber: string | null; isConnected: boolean }}
			) => response.payload,
			providesTags: [ 'baileys-config', ],
		}),
		postWhatsappGroup: builder.mutation<
			{ payload: WhatsappGroup },
			{ name: string }
		>({
			query: (body) => ({
				url: '/whatsapp/group/',
				method: 'POST',
				body,
			}),
			invalidatesTags: [ 'wpp-group', ],
		}),
		getWhatsappGroups: builder.query<WhatsappGroup[], void>({
			query: () => ({
				url: '/whatsapp/group/',
				method: 'GET',
			}),
			providesTags: [ 'wpp-group', ],
		}),
		deleteWhatsappGroup: builder.mutation<void, string>({
			query: (id) => ({
				url: `/whatsapp/group/${id}`,
				method: 'DELETE',
			}),
			invalidatesTags: [ 'wpp-group', ],
		}),
		getUsersByPhoneNumbers: builder.mutation<
			{ payload: User[] },
			string[]
		>({
			query: (phoneNumbers) => ({
				url: `/authentication/user/by-phone-numbers`,
				method: 'POST',
				body: {
					phoneNumbers: phoneNumbers,
				},
			}),
		}),
		putGroup: builder.mutation<
			void,
			{ groupId: string; name?: string; avatar?: string }
		>({
			query: ({ groupId, name, avatar, }) => ({
				url: `/whatsapp/group/${groupId}`,
				method: 'PUT',
				body: {
					name,
					avatar,
				},
			}),
			invalidatesTags: [ 'wpp-group', ],
		}),
		putGroupParticipants: builder.mutation<
			void,
			{ groupId: string; phonesToAdd: string[]; phonesToRemove: string[] }
		>({
			query: ({ groupId, phonesToAdd, phonesToRemove, }) => ({
				url: `/whatsapp/group/participants/${groupId}`,
				method: 'PUT',
				body: {
					phonesToAdd,
					phonesToRemove,
				},
			}),
			invalidatesTags: [ 'wpp-group', ],
		}),
		postSeverity: builder.mutation<
			void,
			Omit<Severity, 'severity_id' | 'client_id' | 'org_id'>
		>({
			query: (body) => ({
				url: '/severity/',
				method: 'POST',
				body,
			}),
			invalidatesTags: [ 'severity', ],
		}),
		patchSeverity: builder.mutation<
			void,
			{
				id: string;
				severity: Omit<
					Severity,
					'severity_id' | 'client_id' | 'org_id' | 'creation_date'
				>;
			}
		>({
			query: ({ id, severity, }) => ({
				url: `/severity/${id}`,
				method: 'PATCH',
				body: severity,
			}),
			invalidatesTags: [ 'severity', ],
		}),
		getSeverities: builder.query<Severity[], void>({
			query: () => ({
				url: '/severity/',
				method: 'GET',
			}),
			providesTags: [ 'severity', ],
		}),
		deleteSeverity: builder.mutation<void, string>({
			query: (id) => ({
				url: `/severity/${id}`,
				method: 'DELETE',
			}),
			invalidatesTags: [ 'severity', ],
		}),
		postTestSqlObserver: builder.mutation<
			{
				payload: {
					id: string;
				};
			},
			{
				query: string;
				id: string;
			}
		>({
			query: (body) => ({
				url: '/observers/test-sql',
				method: 'POST',
				body,
			}),
		}),
		// Preview do relatório: a API devolve o mesmo HTML do anexo, com a amostra do teste de SQL
		postReportPreview: builder.mutation<
			{ payload: { html: string }},
			{
				name: string;
				report: Pick<ObserverReport, 'blocks' | 'joins'>;
				data: Record<string, Record<string, unknown>[]>;
			}
		>({
			query: (body) => ({
				url: '/observers/report-preview',
				method: 'POST',
				body,
			}),
		}),
		postObserver: builder.mutation<
			{ payload: { observer: Observer }},
			Omit<Observer, 'observer_id' | 'client_id' | 'org_id'>
		>({
			query: (body) => ({
				url: '/observers/',
				method: 'POST',
				body,
			}),
			invalidatesTags: (_, __, arg) => {
				const tags: TagDescription<'observer' | 'module'>[] = [ 'observer', ];
				if (arg.module_id) {
					tags.push('module');
				}
				return tags;
			},
			async onQueryStarted(_, { queryFulfilled, dispatch, }) {
				try {
					await queryFulfilled;
					dispatch(centralApi.endpoints.forceClientReload.initiate());
				} catch (err) {
					console.error('Error occurred in force-client-reload', err);
				}
			},
		}),
		patchObserver: builder.mutation<
			void,
			{
				id: string;
				observer: Partial<Omit<Observer, 'observer_id' | 'client_id' | 'org_id'>>;
			}
		>({
			query: ({ id, observer, }) => ({
				url: `/observers/${id}`,
				method: 'PATCH',
				body: observer,
			}),
			invalidatesTags: [ 'observer', ],
			async onQueryStarted(_, { queryFulfilled, dispatch, }) {
				try {
					await queryFulfilled;
					dispatch(centralApi.endpoints.forceClientReload.initiate());
				} catch (err) {
					console.error('Error occurred in force-client-reload', err);
				}
			},
		}),
		getObservers: builder.query<Observer[], void>({
			query: () => ({
				url: '/observers/',
				method: 'GET',
			}),
			providesTags: [ 'observer', ],
		}),
		getObserverById: builder.query<
			Omit<Observer, 'sql_url' | 'module_sql_url'> & { sql: string | null, module_sql: string | null },
			{ id: string }
		>({
			query: ({ id, }) => ({
				url: `/observers/${id}`,
				method: 'GET',
			}),
		}),
		deleteObserver: builder.mutation<void, string>({
			query: (id) => ({
				url: `/observers/${id}`,
				method: 'DELETE',
			}),
			invalidatesTags: [ 'observer', ],
			async onQueryStarted(_, { queryFulfilled, dispatch, }) {
				try {
					await queryFulfilled;
					dispatch(centralApi.endpoints.forceClientReload.initiate());
				} catch (err) {
					console.error('Error occurred in force-client-reload', err);
				}
			},
		}),
		postObserverWhatsappGroup: builder.mutation<
			void,
			ObserverWhatsappGroup
		>({
			query: (body) => ({
				url: '/observer-whatsapp-group/',
				method: 'POST',
				body,
			}),
			invalidatesTags: [ 'observer-whatsapp-group', ],
			async onQueryStarted(_, { queryFulfilled, dispatch, }) {
				try {
					await queryFulfilled;
					dispatch(centralApi.endpoints.forceClientReload.initiate());
				} catch (err) {
					console.error('Error occurred in force-client-reload', err);
				}
			},
		}),
		getObserverWhatsappGroupById: builder.query<
			ObserverWhatsappGroup[],
			{ id: string }
		>({
			query: ({ id, }) => ({
				url: `/observer-whatsapp-group/${id}`,
				method: 'GET',
			}),
			providesTags: [ 'observer-whatsapp-group', ],
		}),
		deleteObserverWhatsappGroup: builder.mutation<
			void,
			{
				observer_id: string;
				group_id: string;
			}
		>({
			query: ({ observer_id, group_id, }) => ({
				url: `/observer-whatsapp-group/${observer_id}/${group_id}`,
				method: 'DELETE',
			}),
			invalidatesTags: [ 'observer-whatsapp-group', ],
			async onQueryStarted(_, { queryFulfilled, dispatch, }) {
				try {
					await queryFulfilled;
					dispatch(centralApi.endpoints.forceClientReload.initiate());
				} catch (err) {
					console.error('Error occurred in force-client-reload', err);
				}
			},
		}),
		getCustomDashboards: builder.query<CustomDashboard[], void>({
			query: () => ({
				url: '/custom-dashboard/',
				method: 'GET',
			}),
			providesTags: [ 'custom-dashboard', ],
		}),
		postCustomDashboard: builder.mutation<
			void,
			{
				layout: object;
				name: string;
				roles: {
					roles: string[];
				};
				observer_ids: string[];
			}
		>({
			query: (body) => ({
				url: '/custom-dashboard/',
				method: 'POST',
				body,
			}),
			invalidatesTags: [ 'custom-dashboard', ],
		}),
		getCustomDashboardById: builder.query<
			CustomDashboard,
			{ dashboard_id: string }
		>({
			query: ({ dashboard_id, }) => ({
				url: `/custom-dashboard/${dashboard_id}`,
				method: 'GET',
			}),
			providesTags: [ 'custom-dashboard', ],
		}),
		getCustomDashboardObservers: builder.query<
			{ dashboard_id: string; observer_id: string }[],
			{ dashboard_id: string }
		>({
			query: ({ dashboard_id, }) => ({
				url: `/custom-dashboard-observer/${dashboard_id}`,
				method: 'GET',
			}),
			providesTags: [ 'custom-dashboard', ],
		}),
		patchCustomDashboard: builder.mutation<
			void,
			{ dashboard_id: string; body: Partial<CustomDashboard> }
		>({
			query: ({ dashboard_id, body, }) => ({
				url: `/custom-dashboard/${dashboard_id}`,
				method: 'PATCH',
				body,
			}),
			invalidatesTags: [ 'custom-dashboard', ],
		}),
		getOccurrenceByPeriod: builder.query<
			Occurrence[],
			{
				inf_date: string;
				sup_date: string;
			}
		>({
			query: ({ inf_date, sup_date, }) => ({
				url: `/occurrence/find-by-period/${inf_date}/${sup_date}`,
				method: 'GET',
			}),
			providesTags: [ 'occurrence', ],
		}),
		getOccurrenceById: builder.query<
			Occurrence & { ocurrence_data: OcurrenceData },
			{ id: string }
		>({
			query: ({ id, }) => ({
				url: `/occurrence/${id}`,
				method: 'GET',
			}),
		}),
		getLastOcurrenceByObserverId: builder.query<
			Occurrence & { ocurrence_data: OcurrenceData; },
			{ observer_id: string }
		>({
			query: ({ observer_id, }) => ({
				url: `/occurrence/last/${observer_id}`,
				method: 'GET',
			}),
			providesTags: [ 'occurrence', ],
		}),
		deleteCustomDashboard: builder.mutation<void, string>({
			query: (id) => ({
				url: `/custom-dashboard/${id}`,
				method: 'DELETE',
			}),
			invalidatesTags: [ 'custom-dashboard', ],
		}),
		cloneModule: builder.mutation<void, { id: string, observers: Array<string> }>({
			query: ({ id, observers, }) => ({
				url: `/module/clone/${id}`,
				method: 'POST',
				body: { observers, },
			}),
			invalidatesTags: [ 'observer', 'severity', 'periodicity', ],
		}),
		getModules: builder.query<
			ModuleView[],
			Partial<{ page: number; size: number }>
		>({
			query: ({ page, size, }) => ({
				url: `/module${page && size
					? '?'.concat([ `page=${page}`, `size=${size}`, ].join('&'))
					: ''
				}`,
				method: 'GET',
			}),
			providesTags: [ 'module', ],
		}),
		getModuleById: builder.query<ModuleView, { id: string }>({
			query: ({ id, }) => ({
				url: `/module/${id}`,
				method: 'GET',
			}),
		}),
		postModule: builder.mutation<
			void,
			Omit<Module, 'module_id'>
		>({
			query: (body) => ({
				url: '/module',
				method: 'POST',
				body,
			}),
			invalidatesTags: [ 'module', ],
		}),
		patchModule: builder.mutation<
			void,
			{
				id: string;
				body: Partial<Module>
			}
		>({
			query: ({ id, body, }) => ({
				url: `/module/${id}`,
				method: 'PATCH',
				body,
			}),
			invalidatesTags: [ 'module', ],
		}),
		deleteModule: builder.mutation<void, string>({
			query: (id) => ({
				url: `/module/${id}`,
				method: 'DELETE',
			}),
			invalidatesTags: [ 'module', ],
		}),
		getPartners: builder.query<
			Partner[],
			Partial<{ page: number; size: number }>
		>({
			query: ({ page, size, }) => ({
				url: `/partner${page && size
					? '?'.concat([ `page=${page}`, `size=${size}`, ].join('&'))
					: ''
				}`,
				method: 'GET',
			}),
			providesTags: [ 'partners', ],
		}),
		getPartnerById: builder.query<Partner, { id: string }>({
			query: ({ id, }) => ({
				url: `/partner/${id}`,
				method: 'GET',
			}),
		}),
		createPartner: builder.mutation<
			void,
			Omit<Partner, 'id'>
		>({
			query: (body) => ({
				url: '/partner',
				method: 'POST',
				body,
			}),
			invalidatesTags: [ 'partners', ],
		}),
		editPartner: builder.mutation<
			void,
			{
				id: string;
				body: Partial<Partner>
			}
		>({
			query: ({ id, body, }) => ({
				url: `/partner/${id}`,
				method: 'PATCH',
				body,
			}),
			invalidatesTags: [ 'partners', ],
		}),
		deletePartner: builder.mutation<void, string>({
			query: (id) => ({
				url: `/partner/${id}`,
				method: 'DELETE',
			}),
			invalidatesTags: [ 'partners', ],
		}),
		forceObserverExecution: builder.mutation<
			{
				payload: { execution_ok: boolean },
				http_message: string
			},
			string
		>({
			query: (observer_id) => ({
				url: `/client/observer/force-execution/${observer_id}`,
				method: 'POST',
			}),
		}),
		forceClientReload: builder.mutation<void, void>({
			query: () => ({
				url: `/client/force-reload`,
				method: 'POST',
			}),
		}),
		createOrganization: builder.mutation<void, { name: string, partner_id?: string }>({
			query: (body) => ({
				url: '/organization',
				method: 'POST',
				body,
			}),
			invalidatesTags: [ 'organizations', ],
		}),
		deleteOrganization: builder.mutation<
			{ http_message: string, payload: { id: string }},
			{ id: string }
		>({
			query: ({ id, }) => ({
				url: `/organization/${id}`,
				method: 'DELETE',
			}),
			invalidatesTags: [ 'organizations', ],
		}),
		editOrganization: builder.mutation<
			void,
			{
				id: string;
				body: Partial<Organization>
			}
		>({
			query: ({ id, body, }) => ({
				url: `/organization/${id}`,
				method: 'PATCH',
				body,
			}),
			invalidatesTags: [ 'organizations', ],
		}),
		getOrganizations: builder.query<(Organization & { users: [], jwt: string })[], void>({
			query: () => ({
				url: '/organization',
				method: 'GET',
			}),
			providesTags: [ 'all_organizations', ],
		}),
		getOrganizationById: builder.query<Organization & { users: User[], jwt: string }, string>({
			query: (id) => ({
				url: `/organization/${id}`,
				method: 'GET',
			}),
			providesTags: [ 'organizations', ],
		}),
		getLastOccurrenceByObserver: builder.mutation<
			any,
			{ observer_id: string }
		>({
			query: ({ observer_id, }) => ({
				url: `/occurrence/last/${observer_id}`,
				method: 'GET',
			}),

		}),
		getAllUsers: builder.query<User[], void>({
			query: () => ({
				url: '/users',
				method: 'GET',
			}),
			providesTags: [ 'users', ],
		}),
		createUser: builder.mutation<void, { user: Omit<Partial<User>, 'id' | 'enabled' | 'profile_pic'> }
		>({
			query: (body) => ({
				url: '/users',
				method: 'POST',
				body,
			}),
			invalidatesTags: [ 'users', 'organizations', ],
		}),
		editUser: builder.mutation<void, { id: string, user: Partial<User> }
		>({
			query: ({ id, user, }) => ({
				url: `/users/${id}`,
				method: 'PATCH',
				body: user,
			}),
			invalidatesTags: [ 'users', 'organizations', ],
		}),
		deleteUser: builder.mutation<
			{ http_message: string, id: string },
			{ id: string }
		>({
			query: ({ id, }) => ({
				url: `/users/${id}`,
				method: 'DELETE',
			}),
			invalidatesTags: [ 'users', 'organizations', ],
		}),
		authenticateUser: builder.mutation<void, { email: string, password: string }>({
			query: (body) => ({
				url: '/auth',
				method: 'POST',
				body,
			}),
			invalidatesTags: [
				'auth',
				'observer-whatsapp-group',
				'periodicity',
				'zapi-config',
				'occurrence',
				'wpp-group',
				'observer',
				'severity',
				'module',
				'group',
				'users',
				'custom-dashboard',
				'organizations',
			],
		}),
		refreshToken: builder.mutation<void, { token: string }>({
			query: (body) => ({
				url: '/auth/refresh',
				method: 'POST',
				body,
			}),
			async onQueryStarted(_, { queryFulfilled, dispatch, }) {
				try {
					await queryFulfilled;

					setTimeout(() => {
						dispatch(centralApi.util.invalidateTags([
							'observer-whatsapp-group',
							'periodicity',
							'zapi-config',
							'occurrence',
							'wpp-group',
							'observer',
							'severity',
							'module',
							'group',
							'users',
							'custom-dashboard',
							'organizations',
						]));
					}, 1000);
				} catch (error) {
					console.error('Erro ao invalidar tags após revalidar token:', error);
				}
			},
		}),
		changeOrganization: builder.mutation<void, { orgId: string }>({
			query: ({ orgId, }) => ({
				url: `/auth/change-org/${orgId}`,
				method: 'POST',
			}),
			async onQueryStarted(_, { queryFulfilled, dispatch, }) {
				try {
					await queryFulfilled;

					setTimeout(() => {
						dispatch(centralApi.util.invalidateTags([
							'observer-whatsapp-group',
							'periodicity',
							'zapi-config',
							'occurrence',
							'wpp-group',
							'observer',
							'severity',
							'module',
							'group',
							'users',
							'custom-dashboard',
							'organizations',
						]));
					}, 1000);
				} catch (error) {
					console.error('Erro ao invalidar tags após alterar organização:', error);
				}
			},
		}),
	}),
});

export const {
	useAuthenticateUserMutation,
	useRefreshTokenMutation,
	useGetAllUsersQuery,
	useCreateUserMutation,
	useDeleteUserMutation,
	useGetAllPeriodicitiesQuery,
	usePostPeriodicityMutation,
	useDeletePeriodicityMutation,
	usePatchPeriodicityMutation,
	useDeleteZapiConfigMutation,
	useGetZapiConfigsQuery,
	useLazyGetAllPeriodicitiesQuery,
	useLazyGetZapiConfigsQuery,
	usePatchZapiConfigMutation,
	usePostZapiConfigMutation,
	usePostWhatsappGroupMutation,
	useGetWhatsappGroupsQuery,
	useDeleteWhatsappGroupMutation,
	useGetUsersByPhoneNumbersMutation,
	usePutGroupMutation,
	usePutGroupParticipantsMutation,
	useDeleteSeverityMutation,
	useGetSeveritiesQuery,
	usePostSeverityMutation,
	usePatchSeverityMutation,
	useDeleteObserverMutation,
	useGetObserversQuery,
	useLazyGetObserverByIdQuery,
	usePostObserverMutation,
	usePatchObserverMutation,
	usePostTestSqlObserverMutation,
	usePostReportPreviewMutation,
	useLazyGetObserverWhatsappGroupByIdQuery,
	usePostObserverWhatsappGroupMutation,
	useDeleteObserverWhatsappGroupMutation,
	useGetCustomDashboardsQuery,
	usePostCustomDashboardMutation,
	useGetCustomDashboardByIdQuery,
	useGetCustomDashboardObserversQuery,
	usePatchCustomDashboardMutation,
	useLazyGetOccurrenceByPeriodQuery,
	useGetOccurrenceByIdQuery,
	useLazyGetLastOcurrenceByObserverIdQuery,
	useDeleteCustomDashboardMutation,
	useGetModulesQuery,
	useGetModuleByIdQuery,
	usePostModuleMutation,
	useDeleteModuleMutation,
	usePatchModuleMutation,
	useCloneModuleMutation,
	useForceObserverExecutionMutation,
	useCreateOrganizationMutation,
	useDeleteOrganizationMutation,
	useGetLastOccurrenceByObserverMutation,
	useGetPartnersQuery,
	useGetPartnerByIdQuery,
	useCreatePartnerMutation,
	useEditPartnerMutation,
	useDeletePartnerMutation,
	useGetOrganizationsQuery,
	useEditOrganizationMutation,
	useGetOrganizationByIdQuery,
	useEditUserMutation,
	useChangeOrganizationMutation,
	useGetBaileysConfigQuery,
	useLazyGetBaileysConfigQuery,
	usePostBaileysConfigMutation,
	useDeleteBaileysConfigMutation,
	useRequestBaileysPairingCodeMutation,
	useGetBaileysQRCodeQuery,
	useLazyGetBaileysQRCodeQuery,
	useGetBaileysStatusQuery,
	usePostBaileysAddToGroupsMutation,
	useGetBaileysPartnerInfoQuery,
} = centralApi;